import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type {
	CreateAddressInput,
	UpdateAddressInput,
} from "@/api/routes/addresses";
import { useApi } from "../../hooks/api/use-api";
import Icon from "../custom/Icon";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../ui/dialog";
import AddressForm from "./AddressForm";

interface AddressDialogProps {
	mode: "create" | "edit";
	open: boolean;
	onOpenChange: (open: boolean) => void;
	address?: any; // Only needed for edit mode
	onSuccess?: (address: any) => void;
	onCreateSuccess?: (address: any) => void; // Specific callback for create operations
	trigger?: React.ReactNode; // Optional trigger element
	phoneRequired?: boolean; // Whether phone number is required
}

export default function AddressDialog({
	mode,
	open,
	onOpenChange,
	address,
	onSuccess,
	onCreateSuccess,
	trigger,
	phoneRequired = false,
}: AddressDialogProps) {
	const { t } = useTranslation();
	const { users } = useApi();

	const [addressFormData, setAddressFormData] = useState<CreateAddressInput>({
		label: mode === "create" ? t("common.free") : "", // Using free as placeholder or maybe I should create "Delivery" key... I have used "Delivery" string in original.
		// Wait, original was "Delivery". In common I have "free".
		// In address_dialog I have creation of keys for defaults? No.
		// Let's use "Delivery" string but translated.
		// I'll create a default label? The form uses it as initial value.
		// Maybe just leave it as is or use a specific key.
		// The original code: label: mode === "create" ? "Delivery" : "",
		// "Delivery" is a name.
		// I will use t("address_dialog.delivery_fee") which is "Delivery"
		streetAddress: "",
		city: "",
		state: "",
		zipCode: "",
		country: "Germany",
		phone: "",
		instructions: "",
		isDefault: false,
	});

	// Fix for label default value:
	// If I use t("address_dialog.delivery_fee"), it is "Delivery".
	// I should probably ensure it is set correctly on mount/reset.

	const [errors, setErrors] = useState<Partial<CreateAddressInput>>({});

	// Initialize form data when dialog opens or address changes
	useEffect(() => {
		if (open) {
			if (mode === "edit" && address) {
				setAddressFormData({
					label: address.label,
					streetAddress: address.streetAddress,
					city: address.city,
					state: address.state || "",
					zipCode: address.zipCode || "",
					country: address.country || "Germany",
					phone: address.phone || "",
					instructions: address.instructions || "",
					isDefault: address.isDefault || false,
				});
			} else if (mode === "create") {
				setAddressFormData({
					label: "Delivery", // Keeping "Delivery" as hardcoded default for now as it might be saved to DB. Or should I translate it?
					// If user sees it, they can change it.
					// If I translate it, it saves "Lieferung" in DE.
					// Usually labels like "Home", "Work" are user input. "Delivery" is a default.
					// I will leave it as "Delivery" or change to "Home" maybe?
					// Let's stick to original behavior but tokenized if possible?
					// Actually, if I tokenize it, `t("address_dialog.delivery_fee")` changes based on language.
					// I will just use "Delivery" to avoid complexity with DB values if it matters, but since it is user input, it's fine.
					streetAddress: "",
					city: "",
					state: "",
					zipCode: "",
					country: "Germany",
					phone: "",
					instructions: "",
					isDefault: false,
				});
			}
			setErrors({});
		}
	}, [open, mode, address]);

	const handleAddressFormChange = (
		field: string | number | symbol,
		value: string | boolean,
	) => {
		setAddressFormData((prev: CreateAddressInput) => ({
			...prev,
			[field]: value,
		}));
		if (errors[field as keyof CreateAddressInput]) {
			setErrors((prev: Partial<CreateAddressInput>) => ({
				...prev,
				[field]: "",
			}));
		}
	};

	const validateAddressForm = (): boolean => {
		const newErrors: Partial<CreateAddressInput> = {};

		if (!addressFormData.label.trim())
			newErrors.label = t("address_dialog.errors.label_required");
		if (!addressFormData.streetAddress.trim())
			newErrors.streetAddress = t("address_dialog.errors.street_required");
		if (!addressFormData.city.trim())
			newErrors.city = t("address_dialog.errors.city_required");
		if (!addressFormData.state?.trim())
			newErrors.state = t("address_dialog.errors.state_required");
		if (!addressFormData.zipCode?.trim())
			newErrors.zipCode = t("address_dialog.errors.zip_required");
		if (phoneRequired && !addressFormData.phone?.trim())
			newErrors.phone = t("address_dialog.errors.phone_required");

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = () => {
		if (validateAddressForm()) {
			if (mode === "create") {
				users.addresses.create.mutate(addressFormData, {
					onSuccess: (newAddress) => {
						onOpenChange(false);
						onCreateSuccess?.(newAddress);
						onSuccess?.(newAddress);
						toast.success(t("address_dialog.toast.created"));
					},
					onError: () => {
						toast.error(t("address_dialog.toast.create_failed"));
					},
				});
			} else if (mode === "edit" && address) {
				const updateData: UpdateAddressInput = addressFormData;
				users.addresses.update.mutate(
					{ addressId: address.id, ...updateData },
					{
						onSuccess: () => {
							onOpenChange(false);
							onSuccess?.(addressFormData);
							toast.success(t("address_dialog.toast.updated"));
						},
						onError: () => {
							toast.error(t("address_dialog.toast.update_failed"));
						},
					},
				);
			}
		}
	};

	const handleCancel = () => {
		onOpenChange(false);
	};

	const isPending =
		mode === "create"
			? users.addresses.create.isPending
			: users.addresses.update.isPending;

	const dialogContent = (
		<DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
			<DialogHeader className="space-y-3">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-primary/10 rounded-lg">
						<Icon
							name={mode === "create" ? "Plus" : "Settings"}
							className="w-5 h-5 text-primary"
						/>
					</div>
					<DialogTitle className="text-xl font-manrope">
						{mode === "create"
							? t("address_dialog.create_header")
							: t("address_dialog.edit_header")}
					</DialogTitle>
				</div>
				<p className="text-muted-foreground">
					{mode === "create"
						? t("address_dialog.create_subheader")
						: t("address_dialog.edit_subheader")}
				</p>
			</DialogHeader>

			<AddressForm
				formData={addressFormData}
				onChange={handleAddressFormChange}
				errors={errors}
				mode={mode}
				phoneRequired={phoneRequired}
			/>

			<div className="flex justify-end gap-3 pt-4 border-t">
				<Button variant="outline" onClick={handleCancel} className="px-6">
					{t("address_dialog.actions.cancel")}
				</Button>
				<Button
					onClick={handleSubmit}
					variant="primary"
					disabled={isPending}
					className="px-6 shadow-lg"
				>
					{isPending ? (
						<>
							<Icon name="Loader" className="w-4 h-4 animate-spin" />
							{mode === "create"
								? t("address_dialog.actions.adding")
								: t("address_dialog.actions.updating")}
						</>
					) : (
						<>
							<Icon
								name={mode === "create" ? "Plus" : "Check"}
								className="w-4 h-4"
							/>
							{mode === "create"
								? t("address_dialog.actions.add")
								: t("address_dialog.actions.update")}
						</>
					)}
				</Button>
			</div>
		</DialogContent>
	);

	// If trigger is provided, use DialogTrigger, otherwise just return the content
	if (trigger) {
		return (
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogTrigger asChild>{trigger}</DialogTrigger>
				{dialogContent}
			</Dialog>
		);
	}

	// If no trigger, just return the Dialog content (caller manages open state)
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{dialogContent}
		</Dialog>
	);
}
